import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useEffect,
} from "react";
import { User } from "../types"; // Import User type from central types file

// Define the shape of the user object (adjust based on actual API response)
// interface User { ... } // REMOVED local definition

// Define the shape of the auth context state
interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean; // To track initial loading state
  login: (userData: User & { token: string }) => void;
  logout: () => void;
  register: (userData: User & { token: string }) => void; // Assuming register also returns user+token
  setUser: (user: User | null) => void; // Allow updating user state
}

// Create the context with a default value
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Define the props for the provider
interface AuthProviderProps {
  children: ReactNode;
}

// Create the provider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true); // Start loading true

  useEffect(() => {
    // Check localStorage for existing user info on initial load
    setLoading(true);
    const storedUserInfo = localStorage.getItem("userInfo");
    if (storedUserInfo) {
      try {
        const userInfo: User & { token: string } = JSON.parse(storedUserInfo);
        if (userInfo && userInfo.token) {
          setUser(userInfo); // Set user state
          setToken(userInfo.token); // Set token state
          // Axios interceptor already handles setting header for subsequent requests
        }
      } catch (e) {
        console.error("Failed to parse user info from storage", e);
        localStorage.removeItem("userInfo"); // Clear corrupted data
      }
    }
    setLoading(false); // Finished loading initial state
  }, []);

  // Login function
  const login = (userData: User & { token: string }) => {
    localStorage.setItem("userInfo", JSON.stringify(userData));
    setUser(userData);
    setToken(userData.token);
    // Axios interceptor will pick up the token from localStorage for next requests
  };

  // Register function (similar to login for state management)
  const register = (userData: User & { token: string }) => {
    // Typically after registration, the user might be logged in automatically
    // or redirected to login. Assuming auto-login here.
    localStorage.setItem("userInfo", JSON.stringify(userData));
    setUser(userData);
    setToken(userData.token);
  };

  // Logout function
  const logout = () => {
    localStorage.removeItem("userInfo");
    setUser(null);
    setToken(null);
    // Optionally clear other state or redirect
    // Axios interceptor won't add token anymore
  };

  // Value provided by the context
  const value = {
    user,
    token,
    loading,
    login,
    logout,
    register,
    setUser, // Expose setUser if direct manipulation is needed
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use the auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
